import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { snakeCase } from 'lodash';
import { RootState, AppDispatch, getClient, bearerAuth, validationErrors } from '@src/core/store';
import {
  Order,
  Voucher,
  SaleFormInput,
  SaleFormInputRequest,
  ErrorResponse,
  SaleChangeTypeEnum,
} from '@src/core/buyerApiClient';
import { DirectLinkType } from '@src/domains/direct-links';
import { summarizeVouchers } from '@src/domains/vouchers';
import {
  organizeFormInputs,
  indexFormInputErrorsByHash,
  countFormInputs,
  getOrderSaleItems,
  hasPendingFormInputs,
  findDirectLinkOrder,
  isOpenOrder,
  OPEN_ORDER_STATUSES,
} from '@src/domains/orders';
import { publicApiTokenSelector } from '@src/stores/app';
import { setCouponErrors } from '@src/features/cart/store/cart';
import { CouponErrorEnum, type CouponErrorsType } from '@src/features/coupons/core/couponErrors';
import { CheckoutErrors, setErrors as setCheckoutErrors } from '@src/stores/checkout';
import * as storage from '@src/common/storage';
import { getPaginationTotalCount, getStoredUtms } from '@src/common';

const planneWidgetOrderReturnKey = 'planneWidgetOrderReturn';

export const PENDING = 'pending';
export const SENDING = 'sending';
export const COMPLETED = 'completed';
export type OrderFormInputsState = 'pending' | 'sending' | 'completed';

export interface OrderState {
  pendingOrders?: Order[];
  currentOrder?: Order;
  orderVouchersIndex: { [key: string]: Voucher[] };
  orderOriginalAmountIndex: Record<string, number>;
  orderRequiredFormInputs?: OrderFormInputs;
  paginatedOrders: PaginatedOrders;
  skipExternalAuth: boolean;
  isLoadingOrders: boolean;
}

interface OrderFormInputs {
  inputs: Array<SaleFormInput[]>;
  state: OrderFormInputsState;
  errors: RequiredFormInputsErrors;
  inputsToSend?: FinalSaleRequiredFormInput[];
}

export interface RequiredFormInputsErrors {
  [key: string]: string[];
}

export interface FinalSaleRequiredFormInput {
  hash: string;
  value: string;
  inputType: string;
}

interface PaginatedOrders {
  pages: { [key: string]: Order[] };
  totalPages: number;
  totalOrders: number;
}

const initialState: OrderState = {
  orderVouchersIndex: {},
  orderOriginalAmountIndex: {},
  paginatedOrders: { pages: {}, totalPages: 1, totalOrders: 0 },
  skipExternalAuth: false,
  isLoadingOrders: true,
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setPendingOrders: (state: OrderState, action: PayloadAction<Order[], string>) => {
      state.pendingOrders = action.payload;
    },
    setConfirmedOrder: {
      reducer(state: OrderState, action: PayloadAction<Order, string, { vouchers: Voucher[] }>) {
        state.currentOrder = action.payload;
        state.orderVouchersIndex[action.payload.id] = action.meta.vouchers;
      },
      prepare(payload: Order, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setProcessedOrder: {
      reducer(state: OrderState, action: PayloadAction<Order, string, { vouchers: Voucher[] }>) {
        state.currentOrder = action.payload;
        state.orderVouchersIndex[action.payload.id] = action.meta.vouchers;
      },
      prepare(payload: Order, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setCurrentOrder: {
      reducer(state: OrderState, action: PayloadAction<Order, string, { vouchers: Voucher[] }>) {
        state.currentOrder = action.payload;
        state.orderVouchersIndex[action.payload.id] = action.meta.vouchers;
      },
      prepare(payload: Order, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setOrdersPage: {
      reducer(
        state: OrderState,
        action: PayloadAction<Order[], string, { total: number; page: number; totalOrders: number }>,
      ) {
        state.paginatedOrders.pages[`${action.meta.page}`] = action.payload;
        state.paginatedOrders.totalPages = action.meta.total;
        state.paginatedOrders.totalOrders = action.meta.totalOrders;
      },
      prepare(payload: Order[], total: number, page: number, totalOrders: number) {
        return { payload, meta: { total, page, totalOrders } };
      },
    },
    setOrderRequiredFormInputs: (state: OrderState, action: PayloadAction<Array<SaleFormInput[]>>) => {
      state.orderRequiredFormInputs = {
        inputs: action.payload,
        state: PENDING,
        errors: {},
      };
    },
    setRequiredFormInputsErrors: (state: OrderState, action: PayloadAction<RequiredFormInputsErrors>) => {
      if (state.orderRequiredFormInputs) {
        state.orderRequiredFormInputs.errors = action.payload;
      }
    },
    /** Drops the 422 errors of the inputs the buyer has corrected since. */
    clearRequiredFormInputsErrors: (state: OrderState, action: PayloadAction<string[]>) => {
      if (state.orderRequiredFormInputs) {
        action.payload.forEach((hash) => delete state.orderRequiredFormInputs!.errors[hash]);
      }
    },
    setOrderRequiredFormInputsState: {
      reducer(state: OrderState, action: PayloadAction<OrderFormInputsState, string, { order: Order }>) {
        if (state.orderRequiredFormInputs) {
          state.orderRequiredFormInputs.state = action.payload;

          // A new send is judged anew; whatever it rejects comes back in its own 422.
          if (action.payload === SENDING) {
            state.orderRequiredFormInputs.errors = {};
          }
        }
      },
      prepare(payload: OrderFormInputsState, order: Order) {
        return { payload, meta: { order } };
      },
    },
    setSkipExternalAuth: (state: OrderState, action: PayloadAction<boolean>) => {
      state.skipExternalAuth = action.payload;
    },
    triggerExternalAuthError: () => {},
    triggerExternalAuthStatus: (_: OrderState, __: PayloadAction<string, string>) => {},
    resetOrder: (state: OrderState) => {
      state.currentOrder = undefined;
    },
    setOrderVouchers: {
      reducer(state: OrderState, action: PayloadAction<Voucher[], string, { order: Order }>) {
        state.orderVouchersIndex[action.meta.order.id] = action.payload;
      },
      prepare(payload: Voucher[], order: Order) {
        return { payload, meta: { order } };
      },
    },
    setIsLoadingOrders: (state: OrderState, action: PayloadAction<boolean>) => {
      state.isLoadingOrders = action.payload;
    },
    setOrderOriginalAmount: {
      reducer(state: OrderState, action: PayloadAction<number, string, { orderId: string }>) {
        state.orderOriginalAmountIndex[action.meta.orderId] = action.payload;
      },
      prepare(payload: number, orderId: string) {
        return { payload, meta: { orderId } };
      },
    },
  },
});

export default orderSlice.reducer;

export const {
  setPendingOrders,
  setConfirmedOrder,
  setCurrentOrder,
  setProcessedOrder,
  setOrdersPage,
  setOrderRequiredFormInputs,
  setRequiredFormInputsErrors,
  clearRequiredFormInputsErrors,
  setOrderRequiredFormInputsState,
  setSkipExternalAuth,
  triggerExternalAuthError,
  triggerExternalAuthStatus,
  resetOrder,
  setOrderVouchers,
  setIsLoadingOrders,
  setOrderOriginalAmount,
} = orderSlice.actions;

export const loadPendingOrders = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;
  if (buyerToken == null) {
    return;
  }

  setTimeout(async () => {
    const { data: orders } = await getClient(state).orders.getOrders(
      0,
      100,
      OPEN_ORDER_STATUSES,
      bearerAuth(buyerToken),
    );
    dispatch(setPendingOrders(orders));
  }, 250);
};

/**
 * A cart link becomes an order the first time its buyer checks out, and checking the cart out again
 * would open another one. So a buyer coming back to the link takes up the order they already have.
 */
export const resumeDirectLinkOrder = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
    directLink: { directLink },
  } = state;

  if (buyerToken == null || directLink?.type !== DirectLinkType.Cart) {
    return;
  }

  const { data: orders } = await getClient(state).orders.getOrders(0, 100, OPEN_ORDER_STATUSES, bearerAuth(buyerToken));
  dispatch(setPendingOrders(orders));

  const linkOrder = findDirectLinkOrder(orders, directLink.code);

  // Read after the request: an order set meanwhile (a 3DS return, say) came with its vouchers and wins.
  if (linkOrder && !getState().order.currentOrder) {
    dispatch(setCurrentOrder(linkOrder, []));
  }
};

export const confirmCartOrder = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
    auth: { buyerToken },
  } = state;

  if (buyerToken == null || cart == null) {
    return;
  }

  const payload = {
    utms: Object.entries(getStoredUtms()).map(([key, value]) => ({ key, value })),
  };

  let resp: AxiosResponse<Order>;
  try {
    resp = await getClient(state).carts.checkoutCart(cart.id, payload, bearerAuth(buyerToken));
  } catch (err) {
    const errors = (err as AxiosError<ErrorResponse>).response!.data?.errors?.map((error) => error);

    const hasCouponErrors = Object.keys(CouponErrorEnum).includes(errors?.[0]?.type ?? '');

    if (hasCouponErrors) {
      const errorKeys = errors!.map(({ type }) => snakeCase(type)) as CouponErrorsType[];

      dispatch(setCouponErrors(errorKeys));
      return;
    }

    const checkoutErrors = errors?.filter((error) => error.type && CheckoutErrors.includes(error.type));

    if (checkoutErrors && checkoutErrors.length) {
      dispatch(setCheckoutErrors(checkoutErrors.map((e) => `checkout_error_${e.type}`)));
    }

    return;
  }

  dispatch(fetchOrderRequiredFormInputs(resp.data, setConfirmedOrder));
};

export const loadReturnOrder = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());
  const id = storage.getValue(publicApiKey!, planneWidgetOrderReturnKey);

  if (id == null) {
    return;
  }

  storage.removeValue(publicApiKey!, planneWidgetOrderReturnKey);
  await loadOrderAsCurrent(dispatch, getState, id, setCurrentOrder);
};

type OrderSetter = typeof setCurrentOrder | typeof setConfirmedOrder | typeof setProcessedOrder;

export const loadOrderAsCurrent = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  id: string,
  setter: OrderSetter,
) => {
  const state = getState();
  const {
    auth: { buyerToken },
    order: { currentOrder },
  } = state;

  if (buyerToken == null) {
    return;
  }

  const { data: order } = await getClient(state).orders.getOrder(id, bearerAuth(buyerToken));

  if (!currentOrder || currentOrder.id === order.id) {
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        try {
          const vouchers = await retrieveAllVouchersAsync(getState, order);
          dispatch(setter(order, vouchers));
        } finally {
          resolve();
        }
      }, 200);
    });
  }

  return order;
};

export const refreshOrderAsCurrent = (orderId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  if (!orderId) return;
  await loadOrderAsCurrent(dispatch, getState, orderId, setCurrentOrder);
};

/** Reads the order without making it current — the caller decides what to do with its status. */
export const fetchOrder = (id: string) => async (_: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if (buyerToken == null) {
    return undefined;
  }

  const { data: order } = await getClient(state).orders.getOrder(id, bearerAuth(buyerToken));
  return order;
};

export const retrieveAllVouchersAsync = async (getState: () => RootState, order: Order) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;
  const client = getClient(state);

  const vouchers: Voucher[] = [];
  const pageSize = 100;

  for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
    const resp = await client.vouchers.getOrderVouchers(order.id, offset, pageSize, bearerAuth(buyerToken!));

    vouchers.push(...resp.data);
    total = getPaginationTotalCount(resp);
  }

  return vouchers;
};

export const loadOrderVouchers = (order: Order) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const vouchers = await retrieveAllVouchersAsync(getState, order);
  dispatch(setOrderVouchers(vouchers, order));
};

export const loadOrderOriginalAmount =
  (orderId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (state.order.orderOriginalAmountIndex[orderId] !== undefined) return;

    const {
      auth: { buyerToken },
    } = state;

    if (buyerToken == null) return;

    const { data: saleChanges } = await getClient(state).saleChanges.getOrderSaleChanges(
      orderId,
      0,
      100,
      SaleChangeTypeEnum.Creation,
      undefined,
      bearerAuth(buyerToken),
    );

    const total = saleChanges.reduce((acc, sc) => acc + sc.finalAmount.amountCents, 0);
    dispatch(setOrderOriginalAmount(total, orderId));
  };

export const setupCheckout = (id: Order['id']) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if (!buyerToken) {
    return;
  }

  const { data: order } = await getClient(state).orders.getOrder(id, bearerAuth(buyerToken));
  dispatch(fetchOrderRequiredFormInputs(order));
};

export const loadOrders = (page: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;
  if (buyerToken == null) {
    return;
  }

  dispatch(setIsLoadingOrders(true));

  try {
    const pageSize = 10;
    const offset = (page - 1) * pageSize;
    const limit = page * pageSize;
    const response = await getClient(state).orders.getOrders(offset, limit, undefined, bearerAuth(buyerToken));
    const totalOrders = getPaginationTotalCount(response);
    const totalPages = Math.ceil(totalOrders / pageSize);

    dispatch(setOrdersPage(response.data, totalPages, page, totalOrders));
  } finally {
    dispatch(setIsLoadingOrders(false));
  }
};

export const refreshOrderPageContainingItem =
  (saleItemId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { order } = getState();
    const pageEntries = Object.entries(order.paginatedOrders.pages);

    const reschedulePage = pageEntries.reduce(
      (curr: string | undefined, [page, orders]) =>
        (orders ?? []).some((order) => getOrderSaleItems(order).some((item) => item.id === saleItemId)) ? page : curr,
      undefined,
    );

    if (reschedulePage) {
      dispatch(loadOrders(parseInt(reschedulePage)));
    }
  };

export const fetchOrderRequiredFormInputs =
  (order: Order, setter?: OrderSetter) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      auth: { buyerToken },
    } = state;

    if (buyerToken == null) {
      return;
    }

    const formInputs: SaleFormInput[] = [];
    const pageSize = 100;
    const client = getClient(state);

    for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
      const resp = await client.formInputs.getOrderRequiredFormInputs(
        order.id,
        offset,
        pageSize,
        bearerAuth(buyerToken),
      );

      formInputs.push(...resp.data);
      total = getPaginationTotalCount(resp);
    }

    dispatch(setOrderRequiredFormInputs(organizeFormInputs(formInputs)));
    await loadOrderAsCurrent(dispatch, getState, order.id, setter ?? setCurrentOrder);
  };

export const sendOrderRequiredFormInput =
  (formInputs: SaleFormInputRequest[], order: Order) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      auth: { buyerToken },
      order: { currentOrder },
    } = state;

    if (buyerToken == null) {
      return;
    }

    dispatch(setOrderRequiredFormInputsState(SENDING, order));

    try {
      await getClient(state).formInputs.setOrderRequiredFormInputs(
        order.id,
        { inputs: formInputs },
        bearerAuth(buyerToken),
      );
    } catch (err) {
      dispatch(setOrderRequiredFormInputsState(PENDING, order));

      const errors = validationErrors(err);
      if (errors) {
        dispatch(setRequiredFormInputsErrors(indexFormInputErrorsByHash(errors, formInputs)));
      } else {
        throw err;
      }

      return;
    }

    dispatch(setOrderRequiredFormInputsState(COMPLETED, order));
    loadOrderAsCurrent(dispatch, getState, order.id, setCurrentOrder);
  };

// Selectors

export const orderRequiredFormInputsSelector = (state: RootState) =>
  state.order.orderRequiredFormInputs
    ? {
        ...state.order.orderRequiredFormInputs,
        qtd: countFormInputs(state.order.orderRequiredFormInputs.inputs),
      }
    : null;

export const hasPendingSaleRequiredFormInputsSelector = (state: RootState): boolean =>
  hasPendingFormInputs(orderRequiredFormInputsSelector(state));

export const currentOrderSelector = (state: RootState) => state.order.currentOrder;
export const orderVouchersSelector = (id: string) => (state: RootState) => state.order.orderVouchersIndex[id] ?? [];
export const orderHasVouchersSelector = (id: string) => (state: RootState) =>
  orderVouchersSelector(id)(state).length > 0;
export const orderVoucherInfoSelector = (id: string) => (state: RootState) => {
  const { hasMultiple, hasPending } = summarizeVouchers(orderVouchersSelector(id)(state));

  return { hasMultipleVouchers: hasMultiple, hasPendingVouchers: hasPending };
};

export const pendingOrdersSelector = (state: RootState) => {
  const {
    order: { pendingOrders },
  } = state;

  return pendingOrders ? pendingOrders.filter(isOpenOrder) : pendingOrders;
};
export const ordersPageSelector = (page: number) => (state: RootState) => {
  const { pages, totalPages, totalOrders } = state.order.paginatedOrders;
  const orders = pages[`${page}`];

  return { orders, totalPages, totalOrders };
};

export const ordersSelector = (state: RootState) => {
  const { pages, totalPages, totalOrders } = state.order.paginatedOrders;

  const orders = Object.values(pages).reduce((orders, page) => [...orders, ...page], []);
  return { orders, totalPages, totalOrders };
};

export const totalOrdersSelector = (state: RootState) => {
  const { totalOrders } = state.order.paginatedOrders;

  return totalOrders;
};

export const isLoadingOrdersSelector = (state: RootState) => state.order.isLoadingOrders;

export const orderOriginalAmountSelector = (orderId: string) => (state: RootState) =>
  state.order.orderOriginalAmountIndex[orderId];
